import './GlobalBanner.css';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { useContext, useEffect, useState } from 'react';
import { Context } from '../../AuthContext';

const BANNER_STORAGE_KEY = 'global-banner-dismissed';

const getTrialDaysLeft = (trialEndsAt) => {
  const now = new Date();
  const end = new Date(trialEndsAt);
  const msLeft = end.getTime() - now.getTime();

  return Math.ceil(msLeft / (1000 * 60 * 60 * 24));
};

const GlobalBanner = () => {
  const { user } = useContext(Context);
  const [isVisible, setIsVisible] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [bannerStorageKey, setBannerStorageKey] = useState(BANNER_STORAGE_KEY);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsVisible(false);
      setTrialDaysLeft(null);
      return;
    }

    let isMounted = true;

    const fetchSubscriptionDetails = async () => {
      const token = await user.getIdToken();
      const response = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'subscription_details', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const res = await response.json();
      const details = res?.data;

      if (!isMounted) {
        return;
      }

      const daysLeft = details?.trial_end ? getTrialDaysLeft(details.trial_end) : null;
      const storageKey = details?.trial_end
        ? `${BANNER_STORAGE_KEY}-${details.trial_end}`
        : BANNER_STORAGE_KEY;

      setBannerStorageKey(storageKey);

      const shouldDisplay =
        details?.subscription_status === 'trialing' &&
        !details?.has_payment_method &&
        daysLeft !== null &&
        daysLeft >= 0 &&
        daysLeft <= 3 &&
        localStorage.getItem(storageKey) !== 'true';

      setTrialDaysLeft(shouldDisplay ? daysLeft : null);
      setIsVisible(shouldDisplay);
    };

    fetchSubscriptionDetails().catch(() => {
      if (isMounted) {
        setIsVisible(false);
        setTrialDaysLeft(null);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem(bannerStorageKey, 'true');
    setIsVisible(false);
  };

  const manageSubscription = async () => {
    if (!user || openingPortal) {
      return;
    }

    setOpeningPortal(true);

    try {
      const token = await user.getIdToken();
      const response = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'portal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const res = await response.json();
      window.location.href = res.url;
    } finally {
      setOpeningPortal(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  const dayLabel = trialDaysLeft === 1 ? 'day' : 'days';

  return (
    <div className="global-banner-wrapper">
      <div className="global-banner">
        <button
          type="button"
          className="global-banner-close"
          aria-label="Dismiss banner"
          onClick={handleDismiss}
        >
          <CloseIcon sx={{ fontSize: '18px' }} />
        </button>
        <div className="global-banner-title">Free Trial Ending Soon</div>
        <div className="global-banner-message">
          You have {trialDaysLeft} {dayLabel} remaining in your free trial. To continue using Full
          Access without interruption, please{' '}
          <button
            type="button"
            className="global-banner-link"
            onClick={manageSubscription}
            disabled={openingPortal}
          >
            add a payment method
            {openingPortal && (
              <CircularProgress size={12} sx={{ color: '#356AC3', marginLeft: '6px' }} />
            )}
          </button>{' '}
          in your account subscription settings.
        </div>
      </div>
    </div>
  );
};

export default GlobalBanner;
